/**
 * Pagination information included in paginated API responses.
 */
export type PaginationInformation = {
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
};

export class PaginationOptions {
  public static readonly DEFAULT_PAGE_SIZE = 50;
  public static readonly INITIAL_PAGE = 1;

  public readonly page: number;
  public readonly pageSize: number;

  constructor({
    page,
    pageSize,
  }: {
    page?: number;
    pageSize?: number;
  } = {}) {
    this.page = page ?? 1;
    this.pageSize = pageSize ?? PaginationOptions.DEFAULT_PAGE_SIZE;
  }

  public toQueryParams(): URLSearchParams {
    return new URLSearchParams({
      page: this.page.toString(),
      page_size: this.pageSize.toString(),
    });
  }
}

type PaginatedQueryState = {
  page: number;
  pagination: { total: number; per_page: number } | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  nextPage: () => void;
  previousPage: () => void;
  isFetching: boolean;
  isPlaceholderData: boolean;
};

/**
 * A `usePaginatedQuery` result as the `pagination` prop a `Table` expects. Next/previous stay
 * inert until the page settles, so a second click can't land on placeholder data and skip one.
 */
export function toTablePagination(query: PaginatedQueryState, rowCount: number) {
  const { pagination, page } = query;

  const counts =
    pagination?.per_page && rowCount
      ? {
          first: pagination.per_page * (page - 1) + 1,
          last: pagination.per_page * (page - 1) + rowCount,
          total: pagination.total,
        }
      : undefined;

  const settled = !query.isPlaceholderData && !query.isFetching;

  return {
    hasNext: settled && query.hasNextPage,
    hasPrevious: settled && query.hasPreviousPage,
    onNext: query.nextPage,
    onPrevious: query.previousPage,
    counts,
  };
}
